import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSideMenuContext } from '../context/sideMenuContext';
import { useNavigateContext } from '../context/NavigateContext';
import { useColorContext } from '../context/colorContext';
import { useUserContext } from '../context/userContext';
import { useUserLimitState } from '../context/user_limit_context';
import { useUserLimitDispatch } from '../context/user_limit_context';
import { isSupabaseConfigured, supabase } from '../services/supabaseClient';
import { useLanguageContext } from '../context/languageContext';
import { FiSettings } from 'react-icons/fi';
import BrandLogo from '../components/ui/BrandLogo';
import {
  LuHouse,
  LuNotebookPen,
  LuChevronLeft,
  LuSquarePen,
  LuLogIn,
  LuEye,
  LuPalette,
  LuHand,
  LuHeadphones,
  LuBookOpenText,
  LuBrain,
  LuZap,
  LuMenu,
  LuX,
} from 'react-icons/lu';

const hexToRgba = (hex, alpha) => {
  const safeHex = /^#([A-Fa-f0-9]{6})$/.test(hex || '') ? hex : '#FFFFFF';
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function SideMenu() {
  const navigate = useNavigate();
  const {
    bgSecondary,
    textPrimary,
    textSecondary,
    activeAccent,
    varkColors,
  } = useColorContext();

  const {
    if_show_side_menu,
    set_if_show_side_menu,
    if_show_view_option,
    if_show_content_option,
    index_btn_main_menus_selected,
    index_view_btn_selected,
    index_content_btn_selected,
  } = useSideMenuContext();

  const {
    setValuesAsHomeView,
    setValuesAsMyNotesView,
    setValuesAsSettingsView,
    setValuesAsSettingsThemeView,
    setValuesAsEditorView,
    setValuesAsAdaptedView,
    setValuesAsAdaptedType,
  } = useNavigateContext();

  const {
    is_authenticated,
    name,
    username,
    resetUserState,
  } = useUserContext();

  const { currentPlanKey } = useUserLimitState();
  const { openUpgradePopup } = useUserLimitDispatch();
  const { t } = useLanguageContext();

  const [isCompactMenu, setIsCompactMenu] = useState(window.innerWidth <= 1200);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isShortViewport, setIsShortViewport] = useState(window.innerHeight <= 860);
  const showLabels = if_show_side_menu;
  const sectionHeaderHeight = 18;
  const mainItemHeight = isShortViewport ? 40 : 44;
  const compactItemHeight = isShortViewport ? 32 : 34;
  const mainIconSize = isShortViewport ? 17 : 19;
  const subIconSize = isShortViewport ? 16 : 18;
  const compactIconSize = isShortViewport ? 14 : 15;
  const mainLabelFontSize = isShortViewport ? 13 : 15;
  const compactLabelFontSize = isShortViewport ? 12 : 13;
  const sectionTitleFontSize = isShortViewport ? 11 : 12;
  const sidebarPaddingY = isShortViewport ? 16 : 24;
  const sidebarPaddingX = isShortViewport ? 10 : 12;
  const sectionGap = isShortViewport ? 28 : 40;
  const itemGap = isShortViewport ? 6 : 8;

  useEffect(() => {
    const handleResize = () => {
      setIsCompactMenu(window.innerWidth <= 1200);
      setIsMobile(window.innerWidth <= 768);
      setIsShortViewport(window.innerHeight <= 860);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const minExpandedWidth = 260;
  const expandedWidth = isCompactMenu ? minExpandedWidth : Math.max(window.innerWidth / 7, minExpandedWidth);
  const collapsedWidth = isCompactMenu ? 56 : 72;
  const isExpanded = if_show_side_menu;

  const menuContainerRef = useRef(null);
  const homeRef = useRef(null);
  const notesRef = useRef(null);
  const editorViewRef = useRef(null);
  const adaptedViewRef = useRef(null);
  const visualRef = useRef(null);
  const kinestheticRef = useRef(null);
  const audioRef = useRef(null);
  const readRef = useRef(null);

  const [mainTop, setMainTop] = useState(null);
  const [viewTop, setViewTop] = useState(null);
  const [contentTop, setContentTop] = useState(null);
  const [mainRectHeight, setMainRectHeight] = useState(36);
  const [viewRectHeight, setViewRectHeight] = useState(36);
  const [contentRectHeight, setContentRectHeight] = useState(36);

  useLayoutEffect(() => {
    if (!menuContainerRef.current || index_btn_main_menus_selected < 0) {
      setMainTop(null);
      return;
    }
    const target = index_btn_main_menus_selected === 0 ? homeRef.current : notesRef.current;
    if (!target) return;
    if (typeof target.offsetTop === 'number' && typeof target.offsetHeight === 'number') {
      setMainTop(target.offsetTop);
      return;
    }
    const containerRect = menuContainerRef.current.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = targetRect.top - containerRect.top;
    setMainTop(top);
  }, [index_btn_main_menus_selected, isCompactMenu]);

  useLayoutEffect(() => {
    if (mainTop === null) return;
    const target = index_btn_main_menus_selected === 0 ? homeRef.current : notesRef.current;
    if (!target || !menuContainerRef.current) return;
    if (typeof target.offsetTop === 'number') {
      setMainTop(target.offsetTop);
    } else {
      const containerRect = menuContainerRef.current.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = targetRect.top - containerRect.top;
      setMainTop(top);
    }
  }, [isExpanded, showLabels]);

  useLayoutEffect(() => {
    if (!menuContainerRef.current || !if_show_view_option || index_view_btn_selected < 0) {
      setViewTop(null);
      return;
    }
    const target = index_view_btn_selected === 0 ? editorViewRef.current : adaptedViewRef.current;
    if (!target) return;
    if (typeof target.offsetTop === 'number' && typeof target.offsetHeight === 'number') {
      setViewTop(target.offsetTop);
      return;
    }
    const containerRect = menuContainerRef.current.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = targetRect.top - containerRect.top;
    setViewTop(top);
  }, [index_view_btn_selected, if_show_view_option, isCompactMenu]);

  useLayoutEffect(() => {
    if (viewTop === null) return;
    const target = index_view_btn_selected === 0 ? editorViewRef.current : adaptedViewRef.current;
    if (!target || !menuContainerRef.current) return;
    if (typeof target.offsetTop === 'number') {
      setViewTop(target.offsetTop);
    } else {
      const containerRect = menuContainerRef.current.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = targetRect.top - containerRect.top;
      setViewTop(top);
    }
  }, [isExpanded, showLabels]);

  useLayoutEffect(() => {
    if (!menuContainerRef.current || !if_show_content_option || index_content_btn_selected < 0) {
      setContentTop(null);
      return;
    }
    const targets = [visualRef, kinestheticRef, audioRef, readRef];
    const target = targets[index_content_btn_selected]?.current;
    if (!target) return;
    if (typeof target.offsetTop === 'number' && typeof target.offsetHeight === 'number') {
      setContentTop(target.offsetTop);
      return;
    }
    const containerRect = menuContainerRef.current.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = targetRect.top - containerRect.top;
    setContentTop(top);
  }, [index_content_btn_selected, if_show_content_option, isCompactMenu]);

  useLayoutEffect(() => {
    if (contentTop === null) return;
    const targets = [visualRef, kinestheticRef, audioRef, readRef];
    const target = targets[index_content_btn_selected]?.current;
    if (!target || !menuContainerRef.current) return;
    if (typeof target.offsetTop === 'number') {
      setContentTop(target.offsetTop);
    } else {
      const containerRect = menuContainerRef.current.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = targetRect.top - containerRect.top;
      setContentTop(top);
    }
  }, [isExpanded, showLabels]);

  const menuBg = bgSecondary;
  const borderColor = hexToRgba(textPrimary, 0.08);
  const selectionBg = hexToRgba(activeAccent, 0.12);
  const selectionGradient = `linear-gradient(270deg, ${activeAccent}, #a855f7, #000000, #a855f7, ${activeAccent})`;
  const selectionBoxShadow = `0 12px 28px ${hexToRgba(activeAccent, 0.22)}`;
  const resolvedUserName = String(name || username || '').trim() || 'User';
  const resolvedPlanLabel = currentPlanKey === 'basic'
    ? 'Basic'
    : currentPlanKey === 'premium'
      ? 'Pro'
      : currentPlanKey === 'vip'
        ? 'Expert'
        : 'Free';

  const closeMobileMenu = () => set_if_show_side_menu(false);
  const withMobileClose = (fn) => () => { if (fn) fn(); if (isMobile) closeMobileMenu(); };

  const handleLogout = async () => {
    try {
      if (isSupabaseConfigured && supabase) {
        await supabase.auth.signOut();
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      resetUserState();
      navigate('/', { replace: true });
    }
  };

  if (isMobile) {
    return (
      <>
        <style>
          {`
            @keyframes ctaGradient {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}
        </style>
        {/* Mobile top bar — always visible, in document flow */}
        <style>{`
          @keyframes mobileMenuFadeIn {
            from { opacity: 0; transform: translateY(-6px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .mobile-menu-content {
            opacity: 0;
            transform: translateY(8px);
            transition: opacity 260ms cubic-bezier(0.22,1,0.36,1), transform 260ms cubic-bezier(0.22,1,0.36,1);
          }

          .mobile-menu-content.enter {
            opacity: 1;
            transform: translateY(0);
          }

          .mobile-menu-item { opacity: 0; transform: translateX(-6px); transform-origin: left center; }
          .mobile-menu-content.enter .mobile-menu-item { opacity: 1; transform: translateX(0); transition: opacity 280ms ease, transform 280ms ease; }
        `}</style>

        <div style={{
          width: '100%',
          height: 52,
          backgroundColor: menuBg,
          borderBottom: `1px solid ${borderColor}`,
          backgroundImage: `radial-gradient(circle at 10% 50%, ${hexToRgba(activeAccent, 0.07)} 0%, transparent 40%)`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          boxSizing: 'border-box',
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
        }}>
          <button
            onClick={() => set_if_show_side_menu(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: textPrimary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              width: 36,
              height: 36,
              borderRadius: 8,
            }}
          >
            <LuMenu size={21} />
          </button>
          <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
            <BrandLogo size={18} />
          </span>
        </div>

        {/* Mobile full-screen overlay — only when expanded */}
        {isExpanded && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: menuBg,
            backgroundImage: `radial-gradient(circle at 10% 0%, ${hexToRgba(activeAccent, 0.11)} 0%, transparent 34%), linear-gradient(180deg, ${hexToRgba(activeAccent, 0.03)} 0%, transparent 28%, transparent 76%, ${hexToRgba(activeAccent, 0.03)} 100%)`,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            // animation helpers for mobile open/close
            animation: 'mobileMenuFadeIn 260ms cubic-bezier(0.22, 1, 0.36, 1) both',
          }}>
            {/* Overlay header */}
            <div style={{
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              flexShrink: 0,
              borderBottom: `1px solid ${borderColor}`,
            }}>
              <BrandLogo size={20} />
              <button
                onClick={closeMobileMenu}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: textPrimary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 8,
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                }}
              >
                <LuX size={21} />
              </button>
            </div>

            {/* Scrollable menu content */}
            <div style={{ flex: 1, padding: '20px 12px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              {/* Main navigation */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 32 }}>
                <MenuItem isExpanded showLabels icon={<LuHouse size={19} />} label={t('nav.home')} isSelected={index_btn_main_menus_selected === 0} onClick={withMobileClose(setValuesAsHomeView)} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                <MenuItem isExpanded showLabels icon={<LuNotebookPen size={19} />} label={t('nav.myNotes')} isSelected={index_btn_main_menus_selected === 1} onClick={withMobileClose(setValuesAsMyNotesView)} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
              </div>

              {/* View section */}
              {if_show_view_option && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: textSecondary, marginBottom: 16, paddingLeft: 12 }}>
                    {t('nav.viewMode')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <MenuItem isExpanded showLabels icon={<LuSquarePen size={18} />} label={t('nav.editorView')} isSelected={index_view_btn_selected === 0} onClick={withMobileClose(setValuesAsEditorView)} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                    <MenuItem isExpanded showLabels icon={<LuBrain size={17} />} label={t('nav.adaptedView')} isSelected={index_view_btn_selected === 1} onClick={withMobileClose(setValuesAsAdaptedView)} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                  </div>
                </div>
              )}

              {/* Content type section */}
              {if_show_content_option && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: textSecondary, marginBottom: 16, paddingLeft: 12 }}>
                    {t('nav.contentType')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <MenuItem isExpanded showLabels icon={<LuEye size={18} />} label={t('nav.mindmaps')} isSelected={index_content_btn_selected === 0} onClick={withMobileClose(() => setValuesAsAdaptedType(0))} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                    <MenuItem isExpanded showLabels icon={<LuHand size={18} />} label={t('nav.flashcards')} isSelected={index_content_btn_selected === 1} onClick={withMobileClose(() => setValuesAsAdaptedType(1))} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                    <MenuItem isExpanded showLabels icon={<LuHeadphones size={18} />} label={t('nav.podcast')} isSelected={index_content_btn_selected === 2} onClick={withMobileClose(() => setValuesAsAdaptedType(2))} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                    <MenuItem isExpanded showLabels icon={<LuBookOpenText size={18} />} label={t('nav.studyNotes')} isSelected={index_content_btn_selected === 3} onClick={withMobileClose(() => setValuesAsAdaptedType(3))} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                  </div>
                </div>
              )}

              {/* Push bottom actions down */}
              <div style={{ flex: 1 }} />

              {/* Bottom actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                <CompactMenuItem isExpanded showLabels icon={<LuZap size={15} />} label={t('nav.upgrade')} variant="upgrade" accentColor={varkColors?.visual || '#7C3AED'} onClick={() => { openUpgradePopup(t('nav.upgradeMessage')); closeMobileMenu(); }} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                <CompactMenuItem isExpanded showLabels icon={<FiSettings size={15} />} label={t('nav.settings')} onClick={withMobileClose(setValuesAsSettingsView)} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
              </div>

              {/* User section */}
              <div style={{ paddingTop: 16, borderTop: `1px solid ${hexToRgba(activeAccent, 0.16)}` }}>
                {is_authenticated ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}>
                    <span title={resolvedUserName} style={{ color: textPrimary, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'calc(100% - 54px)', paddingLeft: 12 }}>
                      {resolvedUserName}
                    </span>
                    <button
                      type="button"
                      onClick={handleLogout}
                      title={t('nav.signOut')}
                      style={{ width: 34, height: 34, border: 'none', borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: textPrimary, background: hexToRgba(activeAccent, 0.18), flexShrink: 0 }}
                    >
                      <LuLogIn size={18} />
                    </button>
                  </div>
                ) : (
                  <MenuItem isExpanded showLabels icon={<LuLogIn size={18} />} label={t('nav.connection')} isSelected={false} isAction onClick={withMobileClose(() => navigate('/connection'))} textPrimary={textPrimary} textSecondary={textSecondary} activeAccent={activeAccent} />
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes ctaGradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
      <div
        ref={menuContainerRef}
        style={{
          width: isExpanded ? expandedWidth : collapsedWidth,
          flexBasis: isExpanded ? expandedWidth : collapsedWidth,
          height: '100vh',
          backgroundColor: menuBg,
          borderRight: `1px solid ${hexToRgba(activeAccent, 0.18)}`,
          boxShadow: 'none',
          backgroundImage: `radial-gradient(circle at 10% 0%, ${hexToRgba(activeAccent, 0.11)} 0%, transparent 34%), linear-gradient(180deg, ${hexToRgba(activeAccent, 0.03)} 0%, transparent 28%, transparent 76%, ${hexToRgba(activeAccent, 0.03)} 100%)`,
          display: 'flex',
          flexDirection: 'column',
          padding: `${sidebarPaddingY}px ${sidebarPaddingX}px`,
          transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1), flex-basis 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          transform: 'translateZ(0)',
          willChange: 'width, flex-basis, transform',
          backfaceVisibility: 'hidden',
          contain: 'layout paint style',
          isolation: 'isolate',
        }}
      >
        {/* Header */}
        <div style={{ position: 'relative', height: 34, marginBottom: isShortViewport ? '18px' : '60px', flexShrink: 0 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0,
            minHeight: 28,
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: showLabels ? 1 : 0,
            maxWidth: showLabels ? Math.max(expandedWidth - 54, 0) : 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            transition: 'opacity 0.28s ease, max-width 0.28s ease',
          }}
        >
          <BrandLogo
            size={22}
            style={{
              transform: showLabels ? 'translateX(0)' : 'translateX(-14px)',
              transition: 'transform 0.28s ease',
            }}
          />
        </span>
        <button
          onClick={() => set_if_show_side_menu(!isExpanded)}
          style={{
            background: 'transparent',
            border: 'none',
            color: textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 8,
            width: 34,
            height: 34,
            borderRadius: 999,
            boxShadow: 'none',
            transition: 'left 0.35s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s ease',
            position: 'absolute',
            top: 0,
            left: isExpanded ? 'calc(100% - 34px)' : 'calc(50% - 17px)',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = textPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = textSecondary;
          }}
        >
          <span style={{ display: 'inline-flex', transition: 'transform 0.28s ease', transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)' }}>
            <LuChevronLeft size={19} />
          </span>
        </button>
      </div>

      {/* Scrollable nav area */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'visible', overflowX: 'hidden' }}>

      {/* Main navigation */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: itemGap, marginBottom: sectionGap, position: 'relative', zIndex: 2 }}>
        {mainTop !== null && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: mainTop,
              height: mainItemHeight,
              background: selectionGradient,
              backgroundSize: '300% 300%',
              borderRadius: 12,
              transition: 'top 0.2s ease, box-shadow 0.28s ease',
              pointerEvents: 'none',
              zIndex: 0,
              boxShadow: selectionBoxShadow,
            }}
          />
        )}
        <MenuItem
          ref={homeRef}
          isExpanded={isExpanded}
          showLabels={showLabels}
          icon={<LuHouse size={mainIconSize} />}
          label={t('nav.home')}
          isSelected={index_btn_main_menus_selected === 0}
          onClick={setValuesAsHomeView}
          textPrimary={textPrimary}
          textSecondary={textSecondary}
          activeAccent={activeAccent}
          itemHeight={mainItemHeight}
          labelFontSize={mainLabelFontSize}
        />
        <MenuItem
          ref={notesRef}
          isExpanded={isExpanded}
          showLabels={showLabels}
          icon={<LuNotebookPen size={mainIconSize} />}
          label={t('nav.myNotes')}
          isSelected={index_btn_main_menus_selected === 1}
          onClick={setValuesAsMyNotesView}
          textPrimary={textPrimary}
          textSecondary={textSecondary}
          activeAccent={activeAccent}
          itemHeight={mainItemHeight}
          labelFontSize={mainLabelFontSize}
        />
      </div>

      {/* View section */}
      {if_show_view_option && (
        <div style={{ opacity: if_show_view_option ? 1 : 0, transition: 'opacity 0.22s', pointerEvents: if_show_view_option ? 'auto' : 'none' }}>
          <div style={{ fontSize: sectionTitleFontSize, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: textSecondary, marginBottom: isShortViewport ? 12 : 20, paddingLeft: 12, textAlign: 'left', height: sectionHeaderHeight, lineHeight: `${sectionHeaderHeight}px` }}>
            <span style={{ opacity: showLabels ? 1 : 0, transform: showLabels ? 'translateX(0)' : 'translateX(-14px)', maxWidth: showLabels ? 180 : 0, transition: 'opacity 0.28s ease, transform 0.28s ease, max-width 0.28s ease', display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', willChange: 'transform, opacity, max-width' }}>
              {t('nav.viewMode')}
            </span>
          </div>
          <div style={{ position: 'relative', zIndex: 2 }}>
            {viewTop !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: viewTop,
                  height: mainItemHeight,
                  background: selectionGradient,
                  backgroundSize: '300% 300%',
                  borderRadius: 12,
                  transition: 'top 0.2s ease, box-shadow 0.28s ease',
                  pointerEvents: 'none',
                  boxShadow: selectionBoxShadow,
                }}
              />
            )}
            <MenuItem
              ref={editorViewRef}
              isExpanded={isExpanded}
              showLabels={showLabels}
              icon={<LuSquarePen size={subIconSize} />}
              label={t('nav.editorView')}
              isSelected={index_view_btn_selected === 0}
              onClick={setValuesAsEditorView}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              activeAccent={activeAccent}
              itemHeight={mainItemHeight}
              labelFontSize={mainLabelFontSize}
            />
            <MenuItem
              ref={adaptedViewRef}
              isExpanded={isExpanded}
              showLabels={showLabels}
              icon={<LuBrain size={subIconSize} />}
              label={t('nav.adaptedView')}
              isSelected={index_view_btn_selected === 1}
              onClick={setValuesAsAdaptedView}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              activeAccent={activeAccent}
              itemHeight={mainItemHeight}
              labelFontSize={mainLabelFontSize}
            />
          </div>
        </div>
      )}

      {/* Content type section */}
      {if_show_content_option && (
        <div style={{ opacity: 1, transition: 'opacity 0.22s', marginTop: isShortViewport ? 24 : 32, pointerEvents: 'auto' }}>
          <div style={{ fontSize: sectionTitleFontSize, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: textSecondary, marginBottom: isShortViewport ? 12 : 20, paddingLeft: 12, textAlign: 'left', height: sectionHeaderHeight, lineHeight: `${sectionHeaderHeight}px` }}>
            <span style={{ opacity: showLabels ? 1 : 0, transform: showLabels ? 'translateX(0)' : 'translateX(-14px)', maxWidth: showLabels ? 180 : 0, transition: 'opacity 0.28s ease, transform 0.28s ease, max-width 0.28s ease', display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', willChange: 'transform, opacity, max-width' }}>
              {t('nav.contentType')}
            </span>
          </div>
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: contentTop ?? 0,
                height: mainItemHeight,
                background: selectionGradient,
                backgroundSize: '300% 300%',
                borderRadius: 12,
                transition: 'top 0.2s ease, opacity 0.28s ease, box-shadow 0.28s ease',
                opacity: contentTop !== null ? 1 : 0,
                pointerEvents: 'none',
                boxShadow: selectionBoxShadow,
              }}
            />
            <MenuItem
              ref={visualRef}
              isExpanded={isExpanded}
              showLabels={showLabels}
              icon={<LuEye size={subIconSize} />}
              label={t('nav.mindmaps')}
              isSelected={index_content_btn_selected === 0}
              onClick={() => setValuesAsAdaptedType(0)}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              activeAccent={activeAccent}
              itemHeight={mainItemHeight}
              labelFontSize={mainLabelFontSize}
            />
            <MenuItem
              ref={kinestheticRef}
              isExpanded={isExpanded}
              showLabels={showLabels}
              icon={<LuHand size={subIconSize} />}
              label={t('nav.flashcards')}
              isSelected={index_content_btn_selected === 1}
              onClick={() => setValuesAsAdaptedType(1)}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              activeAccent={activeAccent}
              itemHeight={mainItemHeight}
              labelFontSize={mainLabelFontSize}
            />
            <MenuItem
              ref={audioRef}
              isExpanded={isExpanded}
              showLabels={showLabels}
              icon={<LuHeadphones size={subIconSize} />}
              label={t('nav.podcast')}
              isSelected={index_content_btn_selected === 2}
              onClick={() => setValuesAsAdaptedType(2)}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              activeAccent={activeAccent}
              itemHeight={mainItemHeight}
              labelFontSize={mainLabelFontSize}
            />
            <MenuItem
              ref={readRef}
              isExpanded={isExpanded}
              showLabels={showLabels}
              icon={<LuBookOpenText size={subIconSize} />}
              label={t('nav.studyNotes')}
              isSelected={index_content_btn_selected === 3}
              onClick={() => setValuesAsAdaptedType(3)}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              activeAccent={activeAccent}
              itemHeight={mainItemHeight}
              labelFontSize={mainLabelFontSize}
            />
          </div>
        </div>
      )}

      </div>{/* End scrollable nav area */}

      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: isShortViewport ? 4 : 6, marginTop: isShortViewport ? 8 : 10, marginBottom: 4 }}>
        <CompactMenuItem
          isExpanded={isExpanded}
          showLabels={showLabels}
          icon={<LuZap size={compactIconSize} />}
          label={t('nav.upgrade')}
          variant="upgrade"
          accentColor={varkColors?.visual || '#7C3AED'}
          onClick={() => openUpgradePopup(t('nav.upgradeMessage'))}
          textPrimary={textPrimary}
          textSecondary={textSecondary}
          activeAccent={activeAccent}
          itemHeight={compactItemHeight}
          labelFontSize={compactLabelFontSize}
        />
        
        <CompactMenuItem
          isExpanded={isExpanded}
          showLabels={showLabels}
          icon={<FiSettings size={compactIconSize} />}
          label={t('nav.settings')}
          onClick={setValuesAsSettingsView}
          textPrimary={textPrimary}
          textSecondary={textSecondary}
          activeAccent={activeAccent}
          itemHeight={compactItemHeight}
          labelFontSize={compactLabelFontSize}
        />
      </div>

      <div style={{ paddingTop: isShortViewport ? 10 : 20, borderTop: `1px solid ${hexToRgba(activeAccent, 0.16)}`, flexShrink: 0 }}>
        {is_authenticated ? (
          <div
            style={{
              position: 'relative',
              display: 'block',
              minHeight: isShortViewport ? 40 : 44,
              height: isShortViewport ? 40 : 44,
            }}
          >
            <span
              title={resolvedUserName}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: showLabels ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-14px)',
                color: textPrimary,
                fontSize: isShortViewport ? 12 : 13,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                opacity: showLabels ? 1 : 0,
                maxWidth: showLabels ? Math.max(expandedWidth - (isShortViewport ? 96 : 110), 72) : 0,
                pointerEvents: 'none',
                transition: 'opacity 0.28s ease, transform 0.28s ease, max-width 0.28s ease',
                willChange: 'opacity, transform, max-width',
              }}
            >
              {resolvedUserName}
            </span>

            <button
              type="button"
              onClick={handleLogout}
              title={t('nav.signOut')}
              style={{
                position: 'absolute',
                top: '50%',
                left: isExpanded ? 'calc(100% - 44px)' : 'calc(50% - 17px)',
                width: isShortViewport ? 32 : 34,
                height: isShortViewport ? 32 : 34,
                border: 'none',
                borderRadius: 10,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: textPrimary,
                background: hexToRgba(activeAccent, 0.18),
                transform: 'translateY(-50%)',
                transition: 'left 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease, color 0.2s ease',
                flexShrink: 0,
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = hexToRgba(activeAccent, 0.3);
                event.currentTarget.style.color = textPrimary;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = hexToRgba(activeAccent, 0.18);
                event.currentTarget.style.color = textPrimary;
              }}
            >
              <LuLogIn size={isShortViewport ? 16 : 18} />
            </button>

          </div>
        ) : (
          <MenuItem
            isExpanded={isExpanded}
            showLabels={showLabels}
            icon={<LuLogIn size={isShortViewport ? 16 : 18} />}
            label={t('nav.connection')}
            isSelected={false}
            isAction={true}
            onClick={() => navigate('/connection')}
            textPrimary={textPrimary}
            textSecondary={textSecondary}
            activeAccent={activeAccent}
          />
        )}
      </div>

    </div>
    </>
  );
}

const MenuItem = React.forwardRef(({ isExpanded, showLabels, icon, label, isSelected, isAction, onClick, textPrimary, textSecondary, activeAccent, itemHeight = 44, labelFontSize = 15 }, ref) => (
  <div
    ref={ref}
    onClick={onClick}
    style={{
      position: 'relative',
      display: 'block',
      height: itemHeight,
      minHeight: itemHeight,
      boxSizing: 'border-box',
      padding: '0 12px',
      borderRadius: 12,
      cursor: 'pointer',
      transition: 'background-color 0.22s ease, color 0.22s ease, transform 0.22s ease',
      color: isSelected ? '#FFFFFF' : (isAction ? textPrimary : textSecondary),
      backgroundColor: 'transparent',
      boxShadow: 'none',
      margin: '2px 0',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.currentTarget.style.color = textPrimary;
        e.currentTarget.style.transform = 'translateX(1px)';
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.currentTarget.style.color = isAction ? textPrimary : textSecondary;
      }
      e.currentTarget.style.transform = 'translateX(0)';
    }}
  >
    <span
      style={{
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: itemHeight <= 40 ? 18 : 20,
        width: 24,
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {typeof icon === 'string' ? icon : icon}
    </span>
    <span
      style={{
        position: 'absolute',
        left: 50,
        right: 12,
        top: '50%',
        fontSize: labelFontSize,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        opacity: showLabels ? 1 : 0,
        maxWidth: showLabels ? 180 : 0,
        transform: showLabels ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-8px)',
        overflow: 'hidden',
        transition: 'opacity 0.28s ease, transform 0.28s ease, max-width 0.28s ease',
        willChange: 'opacity, transform, max-width',
      }}
    >
      {label}
    </span>
  </div>
));

const CompactMenuItem = ({ isExpanded, showLabels, icon, label, trailingText, onClick, textPrimary, textSecondary, activeAccent, variant = 'default', accentColor, itemHeight = 34, labelFontSize = 13 }) => {
  const resolvedAccent = accentColor || activeAccent;
  const isUpgradeVariant = variant === 'upgrade';
  const upgradeGradient = `linear-gradient(270deg, ${resolvedAccent}, #a855f7, #000000, #a855f7, ${resolvedAccent})`;

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'block',
        height: itemHeight,
        minHeight: itemHeight,
        boxSizing: 'border-box',
        padding: '0 12px',
        borderRadius: isUpgradeVariant ? 12 : 10,
        cursor: 'pointer',
        transition: 'background-color 0.22s ease, color 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease',
        color: isUpgradeVariant ? '#FFFFFF' : textSecondary,
        background: isUpgradeVariant ? upgradeGradient : 'transparent',
        backgroundSize: isUpgradeVariant ? '300% 300%' : 'auto',
        animation: isUpgradeVariant ? 'ctaGradient 16s ease infinite' : 'none',
        boxShadow: isUpgradeVariant ? `0 12px 28px ${hexToRgba(resolvedAccent, 0.22)}` : 'none',
        margin: '1px 0',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#FFFFFF';
        e.currentTarget.style.filter = isUpgradeVariant ? 'brightness(1.08) saturate(1.05)' : 'none';
        e.currentTarget.style.boxShadow = isUpgradeVariant ? `0 16px 34px ${hexToRgba(resolvedAccent, 0.28)}` : 'none';
        e.currentTarget.style.transform = 'translateX(1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = isUpgradeVariant ? '#FFFFFF' : textSecondary;
        e.currentTarget.style.filter = 'none';
        e.currentTarget.style.boxShadow = isUpgradeVariant ? `0 12px 28px ${hexToRgba(resolvedAccent, 0.22)}` : 'none';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: itemHeight <= 32 ? 14 : 16,
          width: 20,
          textAlign: 'center',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isUpgradeVariant ? '#FFFFFF' : textSecondary,
        }}
      >
        {typeof icon === 'string' ? icon : icon}
      </span>
      <span
        style={{
          position: 'absolute',
          left: isUpgradeVariant ? (icon ? 40 : 12) : 44,
          right: isUpgradeVariant ? 12 : (trailingText ? 68 : 12),
          top: '50%',
          fontSize: labelFontSize,
          fontWeight: 500,
          textAlign: isUpgradeVariant ? (icon ? 'left' : 'center') : 'left',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          opacity: showLabels ? 1 : 0,
          maxWidth: showLabels ? 180 : 0,
          transform: showLabels ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-8px)',
          overflow: 'hidden',
          transition: 'opacity 0.28s ease, transform 0.28s ease, max-width 0.28s ease',
          willChange: 'opacity, transform, max-width',
          color: isUpgradeVariant ? '#FFFFFF' : textSecondary,
        }}
      >
        {label}
      </span>
      {trailingText ? (
        <span
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: showLabels ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(8px)',
            fontSize: 13,
            fontWeight: 600,
            color: isUpgradeVariant ? '#FFFFFF' : activeAccent,
            opacity: showLabels ? 1 : 0,
            pointerEvents: 'none',
            transition: 'opacity 0.28s ease, transform 0.28s ease',
            maxWidth: showLabels ? 120 : 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {trailingText}
        </span>
      ) : null}
    </div>
  );
};

export default SideMenu;